import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import './Hero.css';
import heroImage from './images/Uvod.webp';
import { ArrowIcon, EASE, Magnetic } from './motion';
import { COMPANY } from '../data/site';

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1, delay, ease: EASE },
});

const Hero = () => {
  const { t, i18n } = useTranslation();
  const ref = useRef(null);
  const lines = t('hero.title', { returnObjects: true });

  // Parallax při odscrollování: fotka zpomaluje a zvětšuje se, text odplouvá a mizí.
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '35%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.65], [1, 0]);

  return (
    <section id="hero" ref={ref} className="hero">
      <motion.div
        className="hero__media"
        initial={{ clipPath: 'inset(10% 8% 10% 8% round 28px)' }}
        animate={{ clipPath: 'inset(0% 0% 0% 0% round 0px)' }}
        transition={{ duration: 1.6, ease: EASE, delay: 0.1 }}
      >
        <motion.div className="hero__parallax" style={{ y: imgY, scale: imgScale }}>
          <motion.img
            src={heroImage}
            alt=""
            initial={{ scale: 1.35 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.2, ease: EASE, delay: 0.1 }}
          />
        </motion.div>
        <div className="hero__shade" />
        <div className="hero__grid" aria-hidden="true" />
      </motion.div>

      <motion.div className="hero__content container" style={{ y: contentY, opacity: contentOpacity }}>
        <motion.p className="eyebrow hero__eyebrow" {...fadeUp(0.6)}>
          {t('hero.eyebrow')}
        </motion.p>

        <h1 className="hero__title" key={i18n.language}>
          {lines.map((line, i) => (
            <span className="mask" key={i}>
              <motion.span
                className={`hero__line ${i === 1 ? 'text-accent' : ''}`}
                initial={{ y: '110%' }}
                animate={{ y: '0%' }}
                transition={{ duration: 1.2, ease: EASE, delay: 0.55 + i * 0.12 }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="hero__row">
          <motion.p className="hero__desc" {...fadeUp(1.05)}>
            {t('hero.desc')}
          </motion.p>
          <motion.div className="hero__ctas" {...fadeUp(1.2)}>
            <Magnetic>
              <Link to="/#contact" className="btn btn--accent">
                {t('hero.ctaPrimary')}
                <ArrowIcon />
              </Link>
            </Magnetic>
            <Link to="/#services" className="btn btn--ghost">
              {t('hero.ctaSecondary')}
            </Link>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        className="hero__bottom container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
      >
        <ul className="hero__meta">
          <li>ISO 9001:2015</li>
          <li>{t('hero.since', { founded: COMPANY.founded })}</li>
          <li>{t('hero.series')}</li>
        </ul>
        <div className="hero__scroll" aria-hidden="true">
          <span>{t('hero.scroll')}</span>
          <i />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
